import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../../utils/api';

const OrderTracker = ({ status }) => {
    const displaySteps = [
        { label: 'Order Placed', match: ['pending_payment', 'paid'] },
        { label: 'Accepted', match: ['accepted'] },
        { label: 'Dispatched', match: ['dispatched'] },
        { label: 'Delivered', match: ['delivered'] }
    ];

    // Helper to check if step is completed
    const isCompleted = (stepIndex) => {
        // Simple logic: if status index is higher than step index
        // But status are strings. Let's map status to priority
        const priorities = {
            'pending_payment': 0,
            'paid': 1,
            'accepted': 2,
            'dispatched': 3,
            'delivered': 4,
            'cancelled': -1
        };
        const currentPriority = priorities[status];
        if (currentPriority === -1) return false; // Cancelled
        return currentPriority >= stepIndex + 1; // +1 because Placed is index 0
    };

    if (status === 'cancelled') {
        return <div className="text-yellow-400 font-bold text-center p-2 rounded bg-red-900/20">ORDER CANCELLED</div>;
    }

    return (
        <div className="w-full py-4">
            <div className="flex items-center justify-between relative">
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-700 -z-10"></div>
                {displaySteps.map((step, index) => {
                    const active = isCompleted(index);
                    return (
                        <div key={index} className="flex flex-col items-center bg-gray-800 px-2">
                            <div className={`w-4 h-4 rounded-full ${active ? 'bg-gold' : 'bg-gray-600'}`}></div>
                            <span className={`text-xs mt-1 ${active ? 'text-gold' : 'text-gray-500'}`}>{step.label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const MainOrders = () => {

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const res = await orderAPI.getPatientOrders();
            if (res.data.success) {
                setOrders(res.data.data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="text-center py-8">Loading orders...</div>;

    return (
        <div className="space-y-6 pb-20">
            <h1 className="text-2xl font-bold text-gold">My Orders</h1>

            {orders.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-xl">
                    <p className="text-gray-400">No orders found</p>
                    <p className="text-sm text-gray-500 mt-2">Go to Products to place an order.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {orders.map((order) => (
                        <div key={order._id} className="bg-gray-800 p-6 rounded-xl shadow-lg">
                            <div className="flex justify-between items-start mb-4 border-b border-gray-700 pb-2">
                                <div>
                                    <h3 className="font-semibold text-lg text-white">Order #{order._id.slice(-6).toUpperCase()}</h3>
                                    <p className="text-xs text-gray-400">
                                        Date: {new Date(order.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-bold text-gold">₹{order.total_amount}</p>
                                    <span className={`text-xs px-2 py-1 rounded ${order.status === 'delivered' ? 'bg-green-900 text-green-300' :
                                        order.status === 'cancelled' ? 'bg-red-900 text-red-300' :
                                            'bg-blue-900 text-blue-300'
                                        }`}>
                                        {order.status.toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            {/* Tracker */}
                            <OrderTracker status={order.status} />

                            {/* Items */}
                            <div className="mt-4 bg-gray-700/50 p-4 rounded-lg">
                                <ul className="space-y-2">
                                    {order.items.map((item, idx) => (
                                        <li key={idx} className="flex justify-between text-sm items-center">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                                                <span className="text-gray-200">{item.name} <span className="text-gray-400">x{item.quantity}</span></span>
                                            </div>
                                            <span className="font-mono">₹{item.price_per_unit * item.quantity}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-3 text-xs text-gray-500 flex justify-between">
                                <span>Store: {order.retailer_id?.name || 'Store'}</span>
                                {order.shipping_address && (
                                    <span>To: {order.shipping_address.city}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MainOrders;
